"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { useSetAtom } from "jotai";
import { X } from "lucide-react";
import { Button, Modal } from "@repo/ui/mantine";
import { Typography } from "@repo/ui/aops-components/typography";
import { removeCartLineAction } from "@/app/actions/remove-cart-line";
import { cartHasCheckoutWarningAtom } from "@/app/atoms/cartHasCheckoutWarning";
import { cartItemCountAtom } from "@/app/atoms/cartItemCount";
import styles from "./RemoveCartLineButton.module.css";
type RemoveCartLineButtonProps = {
  lineId: string;
  productTitle?: string;
};

/**
 * Render the remove cart line button.
 */
export function RemoveCartLineButton(t0) {
  const $ = _c(31);
  const {
    lineId,
    productTitle
  } = t0;
  const router = useRouter();
  const setCartItemCount = useSetAtom(cartItemCountAtom);
  const setCartHasCheckoutWarning = useSetAtom(cartHasCheckoutWarningAtom);
  const [opened, setOpened] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isPending, startTransition] = useTransition();
  const ariaLabel = productTitle ? `Remove ${productTitle} from cart` : "Remove item from cart";
  let t1;
  if ($[0] !== lineId || $[1] !== router || $[2] !== setCartHasCheckoutWarning || $[3] !== setCartItemCount) {
    t1 = function handleConfirm() {
      setErrorMessage(null);
      startTransition(async () => {
        const result = await removeCartLineAction(lineId);
        if (!result.success) {
          setErrorMessage(result.errorMessage);
          return;
        }
        setCartItemCount(result.itemCount);
        setCartHasCheckoutWarning(result.hasCheckoutWarning);
        setOpened(false);
        router.refresh();
      });
    };
    $[0] = lineId;
    $[1] = router;
    $[2] = setCartHasCheckoutWarning;
    $[3] = setCartItemCount;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleConfirm = t1;
  let t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setErrorMessage(null);
      setOpened(true);
    };
    t3 = <X size={20} strokeWidth={1.5} aria-hidden={true} />;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== ariaLabel) {
    t4 = <button type="button" className={styles.removeButton} aria-label={ariaLabel} onClick={t2}>{t3}</button>;
    $[7] = ariaLabel;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isPending) {
    t5 = () => {
      if (!isPending) {
        setOpened(false);
      }
    };
    $[9] = isPending;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Typography id="remove-cart-line-title">Are you sure you wish to remove this item from your cart?</Typography>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== errorMessage) {
    t7 = errorMessage && <Typography className={styles.errorMessage} role="alert">{errorMessage}</Typography>;
    $[12] = errorMessage;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setOpened(false);
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== isPending) {
    t9 = <Button variant="default" onClick={t8} disabled={isPending}>Cancel</Button>;
    $[15] = isPending;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== handleConfirm || $[18] !== isPending) {
    t10 = <Button onClick={handleConfirm} loading={isPending}>Yes, remove</Button>;
    $[17] = handleConfirm;
    $[18] = isPending;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <div className={styles.modalActions}>{t9}{t10}</div>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== opened || $[24] !== t11 || $[25] !== t5 || $[26] !== t7) {
    t12 = <Modal opened={opened} onClose={t5} centered={true} title="Remove item" aria-labelledby="remove-cart-line-title">{t6}{t7}{t11}</Modal>;
    $[23] = opened;
    $[24] = t11;
    $[25] = t5;
    $[26] = t7;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t4) {
    t13 = <>{t4}{t12}</>;
    $[28] = t12;
    $[29] = t4;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
}